import SearchInput from "@/components/common/search-input";
import { useGetUsersAsInfinite } from "@/queries/users/users.api";
import { useGetMembersForGroup } from "@/queries/approval-groups";
import type { UserType } from "@/types/entities/users";
import type { ApprovalUser } from "@/types/entities/approval-groups";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

interface Props {
  // roleId?: string;
  variant?:
    | "default"
    | "secondary"
    | "tertiary"
    | "quiet"
    | "ghost"
    | "ghost-blue"
    | "danger-primary"
    | "danger-secondary"
    | "danger-tertiary"
    | "alpha-neutral";
  selectedUsers: UserType[] | ApprovalUser[];
  onAddUser: (user: UserType | ApprovalUser) => void;
  useApprovalGroupsEndpoint?: boolean; // New prop to use approval groups endpoint
}

const AssignUserDropdown = ({
  onAddUser,
  selectedUsers,
  variant = "secondary",
  useApprovalGroupsEndpoint = false,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [deferredQuery] = useDebounceValue(searchQuery || "", 500);

  // Use different endpoints based on the prop
  const usersData = useGetUsersAsInfinite(
    {
      per_page: 20,
      ...(deferredQuery ? { search: deferredQuery } : {}),
      withoutRole: true,
      relationships: ["createdBy"],
    },
    !useApprovalGroupsEndpoint,
  );

  const approvalGroupsData = useGetMembersForGroup(
    {
      per_page: 20,
      search: deferredQuery || undefined,
    },
    useApprovalGroupsEndpoint,
  );

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useApprovalGroupsEndpoint ? approvalGroupsData : usersData;

  const isLoading = !data;

  const _data = useMemo(() => {
    if (!data) return [];

    if (useApprovalGroupsEndpoint) {
      // For approval groups endpoint, data has a different structure
      return data.pages
        .flatMap((page) => page.data as ApprovalUser[])
        .filter((user) => {
          // Filter out users that are already selected
          if (selectedUsers.some((u) => u.id === user.id)) {
            return false;
          }
          return true;
        });
    } else {
      // For users endpoint, data has pages structure
      return data.pages
        .flatMap((page) => page.data as UserType[])
        .filter((user) => {
          // Filter out users that are already selected
          if (selectedUsers.some((u) => u.id === user.keycloak_id)) {
            return false;
          }
          return true;
        });
    }
  }, [data, selectedUsers, useApprovalGroupsEndpoint]);

  const apiEmpty = (data?.pages?.[0].data.length || 0) === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size="xs" className="h-8" type="button">
          <span className="hidden tablet:inline-flex">Asignar</span>
          <Icon type="far" name="plus" className="text-current" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="size-horizontal-xs max-w-(--size-horizontal-xs) rounded-lg border-neutral-silent text-body-loud font-semibold bg-neutral-object"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <div className="p-3 border-b border-neutral-silent -mx-1 h-15">
          <SearchInput
            className="w-full font-normal"
            variant="md"
            placeholder="Buscar usuarios"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="max-h-55 overflow-auto pt-2 -mx-1">
          <Content
            isLoading={isLoading}
            _data={_data.filter((user) => {
              return !selectedUsers.some((u) => u.id === user.id);
            })}
            // selectedUsers={selectedUsers}
            onAddUser={onAddUser}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            fetchNextPage={() => {
              fetchNextPage();
            }}
            isEmpty={_data.length === 0}
            apiEmpty={apiEmpty}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Content = ({
  isLoading,
  _data,
  onAddUser,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isEmpty,
  apiEmpty = false,
}: {
  isLoading: boolean;
  apiEmpty?: boolean;
  isEmpty: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  _data: (UserType | ApprovalUser)[];
  // selectedUsers: (UserType | ApprovalUser)[];
  onAddUser: (user: UserType | ApprovalUser) => void;
  isFetching: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Icon
          type="far"
          name="spinner-third"
          className="text-on-disabled animate-spin text-2xl"
        />
      </div>
    );
  }

  if (hasNextPage && _data.length === 0) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Button
          variant="ghost-blue"
          size="xs"
          type="button"
          disabled={isFetching}
          onClick={() => {
            if (isFetching) return;
            fetchNextPage();
          }}
        >
          Más resultados
          <Icon
            type="far"
            name={isFetching ? "spinner-third" : "plus"}
            className={cn("text-current", isFetching && "animate-spin")}
          />
        </Button>
      </div>
    );
  }

  if (apiEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay usuarios disponibles
        </span>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay más resultados
        </span>
      </div>
    );
  }

  return (
    <>
      <ul className="px-2 min-h-32">
        {_data?.map((user, index) => {
          const initials = user.name ? `${user.name.charAt(0)}` : "";
          const userId = "userId" in user ? user.userId : user.id;
          return (
            <li key={`${userId}-${index}`}>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                onClick={() => {
                  onAddUser(user);
                }}
              >
                <Avatar size="2xs">
                  <AvatarFallback
                    color="action"
                    className="pointer-events-none"
                    data-testid={`user-avatar-${userId}`}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 flex items-center gap-2 text-base leading-3xl truncate pointer-events-none select-none">
                  <span className="text-body font-semibold whitespace-nowrap">{`${user.name} ${user.last_name}`}</span>
                  <span className="text-body-quiet font-normal truncate">
                    {user.email || "-"}
                  </span>
                </div>

                <div className="pl-3">
                  <Icon
                    type="far"
                    name="plus"
                    className="text-current text-base leading-3xl pointer-events-none"
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <div className="w-full border-t border-neutral-quiet p-3 flex items-center justify-start gap-3">
          <Button
            variant="ghost-blue"
            size="xs"
            type="button"
            disabled={isFetching}
            onClick={() => {
              if (isFetching) return;
              fetchNextPage();
            }}
          >
            Más resultados
            <Icon
              type="far"
              name={isFetching ? "spinner-third" : "plus"}
              className={cn("text-current", isFetching && "animate-spin")}
            />
          </Button>
        </div>
      )}
    </>
  );
};

export default AssignUserDropdown;
